import React, { useEffect, useState } from 'react';
import { IAdditionalFilter, IDataField, IFileInfo, IDataOption, ReportType, IDailyAisSignalsState, FREQUENCY_OPTIONS } from '../../types/reportsTypes';
import { AdditionalFilters } from '../../components/additional-filters/AdditionalFilters';
import { DataSelection } from '../../components/data-selection/DataSelection';
import { DataPreview } from '../../components/data-preview/DataPreview';
import { Divider } from '@heroui/react';
import { CalendarDate } from '@internationalized/date';
import { useAppSelector, useAppDispatch } from '../../../../core/rootHooks';
import { selectSelectedVesselsImoNumbers, selectSelectedVesselsSectors } from '../../../../shared/sharedSelectors';
import { calculateDataExportEstimation } from '../../utils/downloadHelpers';
import { TReportRequestParams } from '../../types/downloadTypes';
import { activityReportActions } from '../../slices/activityReportSlice';
import { selectActivityReportLoading } from '../../slices/activityReportSlice';
import { selectGeneralSeaAreas } from '../../slices/generalSeaAreasSlice';
import {
	areaSignalStatisticsActions,
	selectAreaSignalStatistics,
	selectAreaSignalStatisticsLoading,
} from '../../slices/areaSignalStatisticsSlice';
import { formatDateAsString } from '../../../../utils/helpers';
import { selectDateRangeBoundariesForDailyAisSignals } from '../../selectors/downloadSelectors';
import { addSeascapeLogActivity } from '../../../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../../../activity-logging/activityLogTypes';


const DailyAisSignals: React.FC = () => {
	const { lastDateOfAisData, currentDate } = useAppSelector(selectDateRangeBoundariesForDailyAisSignals);

	// Initialize state with dynamic values based on dateRange
	const getInitialState = (): IDailyAisSignalsState => ({
		additionalFilters: {
			imoNumbers: [],
			sectors: [],
			dateRange: {
				start: new CalendarDate(
					lastDateOfAisData.getFullYear(),
					lastDateOfAisData.getMonth() + 1,
					lastDateOfAisData.getDate(),
				),
				end: new CalendarDate(
					currentDate.getFullYear(),
					currentDate.getMonth() + 1,
					currentDate.getDate(),
				),
			},
			location: {
				type: 'all',
				coordinates: {
					lat: null,
					lon: null,
					latTo: null,
					lonTo: null,
				},
				areaId: [],
			},
		},
		dataFields: [
			{ id: 'mmsi', label: 'MMSI', category: 'Vessel Information', selected: false, columnSizeCsv: 15, order: 1 , groupOrder: 1},
			{ id: 'imo_number', label: 'IMO Number', category: 'Vessel Information', selected: false, columnSizeCsv: 12, order: 2 , groupOrder: 1},
			{ id: 'name', label: 'Vessel Name', category: 'Vessel Information', selected: false, columnSizeCsv: 30, order: 3 , groupOrder: 1},
			
			{ id: 'ship_type', label: 'Ship Type', category: 'Vessel Information', selected: false, columnSizeCsv: 20, order: 4, groupOrder: 1},
			{ id: 'ship_sub_type', label: 'Ship Sub Type', category: 'Vessel Information', selected: false, columnSizeCsv: 25, order: 5, groupOrder: 1 },
			{ id: 'ais_class', label: 'AIS Class', category: 'Vessel Information', selected: false, columnSizeCsv: 12, order: 6, groupOrder: 1 },
			{ id: 'flag', label: 'Flag', category: 'Vessel Information', selected: false, columnSizeCsv: 15, order: 7, groupOrder: 1 },
			{ id: 'draught', label: 'Draught', category: 'Vessel Information', selected: false, columnSizeCsv: 12, order: 20, groupOrder: 1 },
			
			{ id: 'accuracy', label: 'Accuracy', category: 'Signal Quality', selected: false, columnSizeCsv: 10, order: 8, groupOrder: 5 },
			{ id: 'collection_type', label: 'Collection Type', category: 'Signal Quality', selected: false, columnSizeCsv: 18, order: 9, groupOrder: 5 },
			
			{ id: 'destination', label: 'Destination', category: 'Navigation', selected: false, columnSizeCsv: 30, order: 19, groupOrder: 3 },
			{ id: 'course', label: 'Course', category: 'Navigation', selected: false, columnSizeCsv: 12, order: 10, groupOrder: 3 },
			{ id: 'heading', label: 'Heading', category: 'Navigation', selected: false, columnSizeCsv: 12, order: 11, groupOrder: 3 },
			{ id: 'maneuver', label: 'Maneuver', category: 'Navigation', selected: false, columnSizeCsv: 15, order: 14, groupOrder: 3 },
			{ id: 'rot', label: 'Rate of Turn', category: 'Navigation', selected: false, columnSizeCsv: 12, order: 16, groupOrder: 3 },
			{ id: 'speed', label: 'Speed', category: 'Navigation', selected: false, columnSizeCsv: 12, order: 17, groupOrder: 3 },
			{ id: 'navigational_status', label: 'Navigational Status', category: 'Navigation', selected: false, columnSizeCsv: 25, order: 15, groupOrder: 3 },

			{ id: 'latitude', label: 'Latitude', category: 'Position / Time', selected: false, columnSizeCsv: 15, order: 12, groupOrder: 2},
			{ id: 'longitude', label: 'Longitude', category: 'Position / Time', selected: false, columnSizeCsv: 15, order: 13, groupOrder:2},			
			{ id: 'timestamp', label: 'Timestamp', category: 'Position / Time', selected: false, columnSizeCsv: 20, order: 18, groupOrder: 2 },
			{ id: 'eta', label: 'ETA', category: 'Position / Time', selected: false, columnSizeCsv: 20, order: 21, groupOrder: 2 },
			{ id: 'sea_area', label: 'Sea Area', category: 'Position / Time', selected: false, columnSizeCsv: 20, order: 22, groupOrder: 2 },
		],
		dataOptions: {
			frequency: 'daily',
		},
		fileInfo: {
			recordCount: 0,
			vesselCount: 0,
			firstRecord: '-',
			lastRecord: '-',
			estimatedSize: '0.00 MB',
			fileType: 'parquet',
		},
	});

	const [state, setState] = useState<IDailyAisSignalsState>(getInitialState());
	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
	const [areaPercent, setAreaPercent] = useState<any>(null);

	const selectedVesselsImoNumbers = useAppSelector(selectSelectedVesselsImoNumbers);
	const selectedVesselsSectors = useAppSelector(selectSelectedVesselsSectors);
	const generalSeaAreas = useAppSelector(selectGeneralSeaAreas);
	const areaSignalStatistics = useAppSelector(selectAreaSignalStatistics);
	const areaSignalStatisticsLoading = useAppSelector(selectAreaSignalStatisticsLoading);
	const dispatch = useAppDispatch();

	const activityReportLoading = useAppSelector(selectActivityReportLoading);

	// Set up event listener for the Load button
	useEffect(() => {
		const handleLoadEvent = () => {
			handleLoadData();
		};

		window.addEventListener('activity-report:load', handleLoadEvent);

		return () => {
			window.removeEventListener('activity-report:load', handleLoadEvent);
		};
	}, [state]); // Re-add event listener when state changes to capture latest state

	// Handle area signal statistics response and recalculate estimation
	useEffect(() => {
		if (
			state.additionalFilters.location!.type === 'latlong' &&
			state.additionalFilters.location!.coordinates &&
			areaSignalStatistics &&
			areaSignalStatistics.percent &&
			areaPercent !== areaSignalStatistics.percent
		) {
			// Calculate adjusted vessel count based on the percentage from area signal statistics
			const adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (areaSignalStatistics.percent / 100));

			const exportEstimation = calculateEstimationWithAdjustments(adjustedVesselCount);

			setAreaPercent(areaSignalStatistics.percent); // Update area percent state

			setState((prev) => ({
				...prev,
				fileInfo: {
					...prev.fileInfo,
					vesselCount: selectedVesselsImoNumbers.length,
					estimatedSize: exportEstimation.estimatedSize,
					recordCount: exportEstimation.estimatedRecords,
				},
			}));
		}
	}, [
		areaSignalStatistics,
		selectedVesselsImoNumbers.length,
		state.dataFields,
		state.additionalFilters.dateRange,
		state.dataOptions.frequency,
		state.fileInfo.fileType,
		state.additionalFilters.location,
		generalSeaAreas,
	]);

	// Handler functions for state updates
	const handleAdditionalFiltersChange = (filters: IAdditionalFilter) => {
		if (filters.location!.type === 'latlong' && !filters.location!.coordinates) {
			// If location type is latlong but no coordinates are provided, reset the coordinates
			filters.location!.coordinates = {
				lat: -90,
				latTo: 90,
				lon: -180,
				lonTo: 180,
			};
		}

		setState((prev) => ({
			...prev,
			additionalFilters: filters,
		}));
		setIsDataLoaded(false); // Reset data loaded state when filters change
	};

	const handleDataFieldsChange = (dataFields: IDataField[]) => {
		setState((prev) => ({
			...prev,
			dataFields,
		}));
		setIsDataLoaded(false); // Reset data loaded state when data fields change
	};

	const handleDataOptionsChange = (dataOptions: IDataOption) => {
		setState((prev) => ({
			...prev,
			dataOptions,
		}));
		setIsDataLoaded(false); // Reset data loaded state when data options change
	};

	const handleFileInfoChange = (fileInfo: IFileInfo) => {
		setState((prev) => ({
			...prev,
			fileInfo,
		}));

		// If location type is latlong, dispatch area signal statistics request to ensure we have updated data
		if (state.additionalFilters.location!.type === 'latlong' && state.additionalFilters.location!.coordinates) {
			const { lat, lon, latTo, lonTo } = state.additionalFilters.location!.coordinates;

			// Ensure all coordinates are provided and are numbers
			if (
				typeof lat === 'number' &&
				typeof lon === 'number' &&
				typeof latTo === 'number' &&
				typeof lonTo === 'number' &&
				lat !== latTo &&
				lon !== lonTo &&
				!isNaN(lat) &&
				!isNaN(lon) &&
				!isNaN(latTo) &&
				!isNaN(lonTo)
			) {
				// Dispatch area signal statistics request
				dispatch(
					areaSignalStatisticsActions.getAreaSignalStatisticsRequestAction({
						lat,
						latTo,
						lon,
						lonTo,
					}),
				);
			}
		}

		const adjustedVesselCount = calculateAdjustedVesselCount();
		const exportEstimation = calculateEstimationWithAdjustments(
			adjustedVesselCount, // use calculated vessel count
			undefined, // use current date range
			undefined, // use current data fields
			undefined, // use current frequency
			fileInfo.fileType,
		);

		setState((prev) => ({
			...prev,
			fileInfo: {
				...fileInfo,
				estimatedSize: exportEstimation.estimatedSize,
				recordCount: exportEstimation.estimatedRecords,
			},
		}));
	};

	const handleDownload = () => {
		// Create activity report request params from the current state
		// Set end date to end of day (23:59:59) for proper range coverage
		const endDate = state.additionalFilters.dateRange!.end.toDate('UTC');
		endDate.setUTCHours(23, 59, 59, 999);
		
		const requestParams: TReportRequestParams = {
			reportType: ReportType.DailyAisSignals,
			imoList: state.additionalFilters.imoNumbers,
			sectors: state.additionalFilters.sectors!,
			startDate: state.additionalFilters.dateRange!.start.toDate('UTC').toISOString() || '',
			endDate: endDate.toISOString() || '',
			requestedColumns: state.dataFields.filter((field) => field.selected).map((field) => field.id),
			frequency: state.dataOptions.frequency!,
			minLat:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lat || -90)) ||
				null,
			maxLat:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.latTo || 90)) ||
				null,
			minLong:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lon || -180)) ||
				null,
			maxLong:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lonTo || 180)) ||
				null,
			seaArea:
				(state.additionalFilters.location!.type === 'seaArea' &&
					state.additionalFilters.location!.areaId?.length &&
					state.additionalFilters.location!.areaId[0]) ||
				'',
			fileType: state.fileInfo.fileType,
		};
				
		const payload = JSON.stringify({ DataType: 'DailyAisSignals', RequestedParams: requestParams });
	
		addSeascapeLogActivity({ eventId:SeascapeActivityLogConstants.MSI_DATA_DOWNLOAD, payload });

		// Dispatch the request action
		dispatch(activityReportActions.submitActivityReportRequestAction(requestParams));
	};

	const dateFormatOptions: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	};

	const calculateAdjustedVesselCount = (locationOverride?: IAdditionalFilter['location']): number => {
		let adjustedVesselCount = selectedVesselsImoNumbers.length;
		const location = locationOverride || state.additionalFilters.location;

		// If location type is seaArea, filter vessel count according to signalPercent
		if (location!.type === 'seaArea' && location!.areaId && generalSeaAreas) {
			const selectedAreaIds = location!.areaId;

			// Find the selected sea areas and calculate sum of signal percentages
			const selectedSeaAreas = generalSeaAreas.filter((area) => selectedAreaIds.includes(area.name));

			if (selectedSeaAreas.length > 0) {
				// Calculate sum of signal percentages across selected areas
				const totalSignalPercent = selectedSeaAreas.reduce((sum, area) => sum + area.signalPercent, 0);

				// Calculate adjusted vessel count based on the sum of signal percentages
				adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (totalSignalPercent / 100));
			}
		} else if (location!.type === 'latlong' && areaSignalStatistics && areaSignalStatistics.percent) {
			// For latlong, we'll use the response from area signal statistics (handled in useEffect)
			adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (areaSignalStatistics.percent / 100));
		}

		return adjustedVesselCount;
	};

	const calculateEstimationWithAdjustments = (
		overrideVesselCount?: number,
		dateRangeOverride?: IAdditionalFilter['dateRange'],
		dataFieldsOverride?: IDataField[],
		frequencyOverride?: IDataOption['frequency'],
		fileTypeOverride?: string,
		locationOverride?: IAdditionalFilter['location'],
	): ReturnType<typeof calculateDataExportEstimation> => {
		const vesselCount = overrideVesselCount ?? calculateAdjustedVesselCount(locationOverride);
		return calculateDataExportEstimation(
			vesselCount,
			dataFieldsOverride || state.dataFields,
			dateRangeOverride || state.additionalFilters.dateRange!,
			frequencyOverride || state.dataOptions.frequency!,
			fileTypeOverride || state.fileInfo.fileType,
		);
	};

	const updateVesselData = () => {
		// If location type is latlong, dispatch area signal statistics request
		if (state.additionalFilters.location!.type === 'latlong' && state.additionalFilters.location!.coordinates) {
			const { lat, lon, latTo, lonTo } = state.additionalFilters.location!.coordinates;

			// Ensure all coordinates are provided and are numbers
			if (
				typeof lat === 'number' &&
				typeof lon === 'number' &&
				typeof latTo === 'number' &&
				typeof lonTo === 'number' &&
				lat !== latTo &&
				lon !== lonTo &&
				!isNaN(lat) &&
				!isNaN(lon) &&
				!isNaN(latTo) &&
				!isNaN(lonTo)
			) {
				// Dispatch area
				dispatch(
					areaSignalStatisticsActions.getAreaSignalStatisticsRequestAction({
						lat,
						latTo,
						lon,
						lonTo,
					}),
				);
			} else {
				return;
			}
		}

		const adjustedVesselCount = calculateAdjustedVesselCount();
		const exportEstimation = calculateEstimationWithAdjustments(adjustedVesselCount);

		setState((prev) => ({
			...prev,
			additionalFilters: {
				...prev.additionalFilters,
				imoNumbers: selectedVesselsImoNumbers,
				sectors: selectedVesselsSectors,
			},
			fileInfo: {
				...prev.fileInfo,
				vesselCount: selectedVesselsImoNumbers.length,
				firstRecord: formatDateAsString(state.additionalFilters.dateRange!.start?.toString(), dateFormatOptions) ?? '-',
				lastRecord: formatDateAsString(state.additionalFilters.dateRange!.end?.toString(), dateFormatOptions) ?? '-',
				estimatedSize: exportEstimation.estimatedSize,
				recordCount: exportEstimation.estimatedRecords,
			},
		}));
	};

	const handleLoadData = () => {
		updateVesselData();
		setIsDataLoaded(true);
	};

	return (
		<div className="activity-report-container flex w-full h-full gap-4">
			{/* Left panel: Additional Filters */}
			<div className="w-2/3 h-full">
				<AdditionalFilters
					filters={state.additionalFilters}
					onChange={handleAdditionalFiltersChange}
					maxDate={currentDate}
					minDate={lastDateOfAisData}
				/>
			</div>

			<Divider orientation="vertical" />

			{/* Middle panel: Data Selection */}
			<div className="w-full h-full">
				<DataSelection
					dataFields={state.dataFields}
					onDataFieldsChange={handleDataFieldsChange}
					dataOptions={state.dataOptions}
					onDataOptionsChange={handleDataOptionsChange}
					frequencyOptions={FREQUENCY_OPTIONS.filter(option => option.key === 'latest' || option.key === 'daily')} // Only show 'latest' and 'daily' options for frequency in this report
				/>
			</div>

			<Divider orientation="vertical" />

			{/* Right panel: Data Preview */}
			<div className="w-2/3 h-full">
				<DataPreview
					fileInfo={state.fileInfo}
					onFileInfoChange={handleFileInfoChange}
					isLoading={activityReportLoading || areaSignalStatisticsLoading}
					onDownload={handleDownload}
					isDataLoaded={isDataLoaded}
					warningMessage='STRICTLY FOR INTERNAL MSI ANALYSIS ONLY!!! DO NOT SEND DATA TO CLIENTS'
				/>
			</div>
		</div>
	);
};

export default DailyAisSignals;
